import { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  List,
  ListItem,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  FormControlLabel,
  Checkbox,
  Alert,
  Slide,
  Snackbar,
} from '@mui/material';
import {
  Search as SearchIcon,
  HowToReg as WalkinIcon,
  LocalShipping as DispatchIcon,
  CheckCircle as DeliverIcon,
  Replay as ReturnIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Circle as DotIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { mobileApi, mobileRequest } from '../../../services/mobile/mobileApi';
import { formatCurrencyAmount } from '../../../utils/currencyUtils';

// ─── API calls using mobileApi ───────────────────────────

const BASE_URL = '/cheque-collections';

const mobileCollectionService = {
  getCollections: async (params = {}) => {
    return mobileRequest(() => mobileApi.get(BASE_URL, { params }));
  },
  getSummary: async () => {
    return mobileRequest(() => mobileApi.get(`${BASE_URL}/summary`));
  },
  getCollectionDetail: async (id) => {
    return mobileRequest(() => mobileApi.get(`${BASE_URL}/${id}`));
  },
  walkinCollection: async (request) => {
    return mobileRequest(() => mobileApi.post(`${BASE_URL}/walkin`, request));
  },
  dispatchCourier: async (request) => {
    return mobileRequest(() => mobileApi.post(`${BASE_URL}/dispatch`, request));
  },
  confirmDelivery: async (request) => {
    return mobileRequest(() => mobileApi.post(`${BASE_URL}/deliver`, request));
  },
  markReturned: async (request) => {
    return mobileRequest(() => mobileApi.post(`${BASE_URL}/return`, request));
  },
};

// ─── Constants ───────────────────────────────────────────

const STATUS_COLORS = {
  Awaiting:    { bg: '#FFF7ED', color: '#C2410C' },
  Dispatched:  { bg: '#EFF6FF', color: '#1D4ED8' },
  Collected:   { bg: '#F0FDF4', color: '#15803D' },
  Returned:    { bg: '#FEF2F2', color: '#B91C1C' },
};

const METHOD_COLORS = {
  'Walk-in': { bg: '#F5F3FF', color: '#7C3AED' },
  Courier:   { bg: '#EFF6FF', color: '#1D4ED8' },
};

const EVENT_COLORS = {
  Created: '#F59E0B',
  Dispatched: '#3B82F6',
  Delivered: '#10B981',
  Collected: '#10B981',
  Returned: '#EF4444',
};

const RETURN_REASONS = [
  'Supplier rejected cheque',
  'Courier failed — address not found',
  'Courier failed — recipient unavailable',
  'Wrong beneficiary name',
  'Cheque expired or damaged',
  'Supplier requested reissuance',
  'Other',
];

const DialogTransition = (props) => <Slide direction="up" {...props} />;

// ─── Component ───────────────────────────────────────────

const MobileChequeCollections = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [activeDialog, setActiveDialog] = useState({ type: null, cheque: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const queryParams = {
    pageNumber: page,
    pageSize: 20,
    ...(search && { search }),
    ...(statusFilter && { status: statusFilter }),
  };

  const { data: collectionsResult, isLoading, refetch } = useQuery({
    queryKey: ['mobileCollections', queryParams],
    queryFn: () => mobileCollectionService.getCollections(queryParams),
  });

  const collections = collectionsResult?.data || [];
  const totalCount = collectionsResult?.totalCount || 0;

  const handleDialogClose = useCallback(() => {
    setActiveDialog({ type: null, cheque: null });
  }, []);

  const handleActionSuccess = useCallback((message) => {
    showSnackbar(message);
    handleDialogClose();
    refetch();
  }, [handleDialogClose, refetch]);

  const formatCurrency = (amount, currency) => {
    const sym = currency || 'AED';
    return `${sym} ${formatCurrencyAmount(amount, currency, { withSymbol: false })}`;
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '';

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#0f172a' }}>
        Cheque Collection
      </Typography>

      {/* Search & Filter */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          placeholder="Search..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          size="small"
          sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#fff' } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <Select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            displayEmpty
            sx={{ borderRadius: 2, backgroundColor: '#fff', fontSize: '0.8rem' }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="awaiting">Awaiting</MenuItem>
            <MenuItem value="dispatched">Dispatched</MenuItem>
            <MenuItem value="collected">Collected</MenuItem>
            <MenuItem value="returned">Returned</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Collection List */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#2563eb' }} />
        </Box>
      ) : collections.length === 0 ? (
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No collections found</Typography>
        </Card>
      ) : (
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <List disablePadding>
            {collections.map((item, index) => {
              const status = item.CollectionStatus || item.collectionStatus || 'Awaiting';
              const method = item.DeliveryMethod || item.deliveryMethod;
              const statusColor = STATUS_COLORS[status] || {};
              const methodColor = METHOD_COLORS[method] || {};
              const chequeNumber = item.ChequeNumber || item.chequeNumber || '';
              const beneficiary = item.BeneficiaryName || item.beneficiaryName || '';
              const amount = item.ChqAmount ?? item.chqAmount ?? 0;
              const currency = item.Currency || item.currency || '';
              const chqDate = item.ChqDate || item.chqDate;

              return (
                <Box key={item.ChequeId || item.chequeId || item.Id || item.id || index}>
                  <ListItem sx={{ px: 2, py: 1.5, display: 'block' }}>
                    {/* Top row: beneficiary + amount */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {beneficiary}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#64748b', mt: 0.25 }}>
                          Chq# {chequeNumber} &bull; {formatDate(chqDate)}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f172a', fontFamily: 'monospace', flexShrink: 0, ml: 1 }}>
                        {formatCurrency(amount, currency)}
                      </Typography>
                    </Box>

                    {/* Bottom row: chips + actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip label={status} size="small" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600, backgroundColor: statusColor.bg, color: statusColor.color }} />
                        {method && (
                          <Chip label={method} size="small" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600, backgroundColor: methodColor.bg, color: methodColor.color }} />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.25 }}>
                        {status === 'Awaiting' && (
                          <>
                            <IconButton size="small" sx={{ color: '#15803D' }} onClick={() => setActiveDialog({ type: 'walkin', cheque: item })}>
                              <WalkinIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" sx={{ color: '#1D4ED8' }} onClick={() => setActiveDialog({ type: 'dispatch', cheque: item })}>
                              <DispatchIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                        {status === 'Dispatched' && (
                          <>
                            <IconButton size="small" sx={{ color: '#15803D' }} onClick={() => setActiveDialog({ type: 'deliver', cheque: item })}>
                              <DeliverIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" sx={{ color: '#B91C1C' }} onClick={() => setActiveDialog({ type: 'return', cheque: item })}>
                              <ReturnIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                        <IconButton size="small" sx={{ color: '#64748b' }} onClick={() => setActiveDialog({ type: 'detail', cheque: item })}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </ListItem>
                  {index < collections.length - 1 && <Divider />}
                </Box>
              );
            })}
          </List>
        </Card>
      )}

      {/* Pagination */}
      {totalCount > 20 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
          <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} sx={{ textTransform: 'none' }}>Previous</Button>
          <Typography variant="body2" sx={{ alignSelf: 'center', color: '#64748b' }}>
            Page {page}
          </Typography>
          <Button size="small" disabled={collections.length < 20} onClick={() => setPage((p) => p + 1)} sx={{ textTransform: 'none' }}>Next</Button>
        </Box>
      )}

      {/* ─── Walk-in Dialog ─── */}
      <WalkinDialog
        open={activeDialog.type === 'walkin'}
        cheque={activeDialog.cheque}
        onClose={handleDialogClose}
        onSuccess={handleActionSuccess}
        onError={(msg) => showSnackbar(msg, 'error')}
        service={mobileCollectionService}
      />

      {/* ─── Courier Dispatch Dialog ─── */}
      <DispatchDialog
        open={activeDialog.type === 'dispatch'}
        cheque={activeDialog.cheque}
        onClose={handleDialogClose}
        onSuccess={handleActionSuccess}
        onError={(msg) => showSnackbar(msg, 'error')}
        service={mobileCollectionService}
      />

      {/* ─── Delivery Confirm Dialog ─── */}
      <DeliveryDialog
        open={activeDialog.type === 'deliver'}
        cheque={activeDialog.cheque}
        onClose={handleDialogClose}
        onSuccess={handleActionSuccess}
        onError={(msg) => showSnackbar(msg, 'error')}
        service={mobileCollectionService}
      />

      {/* ─── Return Dialog ─── */}
      <MobileReturnDialog
        open={activeDialog.type === 'return'}
        cheque={activeDialog.cheque}
        onClose={handleDialogClose}
        onSuccess={handleActionSuccess}
        onError={(msg) => showSnackbar(msg, 'error')}
        service={mobileCollectionService}
      />

      {/* ─── Detail Dialog ─── */}
      <DetailDialog
        open={activeDialog.type === 'detail'}
        cheque={activeDialog.cheque}
        onClose={handleDialogClose}
        service={mobileCollectionService}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ─── Walk-in Collection Dialog ───────────────────────────

const WalkinDialog = ({ open, cheque, onClose, onSuccess, onError, service }) => {
  const [form, setForm] = useState({ collectorName: '', collectorId: '', signatureObtained: false, remarks: '' });
  const [saving, setSaving] = useState(false);

  const handleChange = (field) => (e) => {
    const value = e?.target?.type === 'checkbox' ? e.target.checked : (e?.target ? e.target.value : e);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.collectorName.trim()) { onError('Collector name is required'); return; }
    if (!form.collectorId.trim()) { onError('Collector ID is required'); return; }
    if (!form.signatureObtained) { onError('Signature acknowledgement is required'); return; }

    setSaving(true);
    try {
      const result = await service.walkinCollection({
        chequeId: cheque.ChequeId || cheque.chequeId,
        collectionDate: new Date().toISOString(),
        collectorName: form.collectorName.trim(),
        collectorId: form.collectorId.trim(),
        signatureObtained: form.signatureObtained,
        remarks: form.remarks.trim() || null,
      });
      if (result.success) {
        onSuccess(result.message || 'Walk-in collection recorded');
        setForm({ collectorName: '', collectorId: '', signatureObtained: false, remarks: '' });
      } else {
        onError(result.message || 'Failed to record collection');
      }
    } catch {
      onError('Failed to record collection');
    } finally {
      setSaving(false);
    }
  };

  if (!cheque) return null;
  const chequeNumber = cheque.ChequeNumber || cheque.chequeNumber || '';
  const beneficiary = cheque.BeneficiaryName || cheque.beneficiaryName || '';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" TransitionComponent={DialogTransition}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
        <WalkinIcon sx={{ color: '#15803D' }} />
        <Typography sx={{ fontWeight: 700, flex: 1 }}>Walk-in Collection</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cheque <strong>{chequeNumber}</strong> &mdash; {beneficiary}
        </Typography>
        <Stack spacing={2}>
          <TextField label="Collector Name" value={form.collectorName} onChange={handleChange('collectorName')} required fullWidth size="small" placeholder="Full name of person collecting" />
          <TextField label="Collector ID / Emirates ID" value={form.collectorId} onChange={handleChange('collectorId')} required fullWidth size="small" placeholder="ID number for verification" />
          <FormControlLabel control={<Checkbox checked={form.signatureObtained} onChange={handleChange('signatureObtained')} />} label="Signature obtained from collector" />
          <TextField label="Remarks" value={form.remarks} onChange={handleChange('remarks')} fullWidth size="small" multiline rows={2} placeholder="Any notes (optional)" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', backgroundColor: '#15803D', '&:hover': { backgroundColor: '#166534' } }}>
          {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Mark Collected'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Courier Dispatch Dialog ─────────────────────────────

const DispatchDialog = ({ open, cheque, onClose, onSuccess, onError, service }) => {
  const [form, setForm] = useState({ courierCompany: '', driverName: '', trackingReference: '', remarks: '' });
  const [saving, setSaving] = useState(false);

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.courierCompany.trim()) { onError('Courier company is required'); return; }
    if (!form.driverName.trim()) { onError('Driver name is required'); return; }
    if (!form.trackingReference.trim()) { onError('Tracking reference is required'); return; }

    setSaving(true);
    try {
      const result = await service.dispatchCourier({
        chequeId: cheque.ChequeId || cheque.chequeId,
        dispatchDate: new Date().toISOString(),
        courierCompany: form.courierCompany.trim(),
        driverName: form.driverName.trim(),
        trackingReference: form.trackingReference.trim(),
        remarks: form.remarks.trim() || null,
      });
      if (result.success) {
        onSuccess(result.message || 'Courier dispatch recorded');
        setForm({ courierCompany: '', driverName: '', trackingReference: '', remarks: '' });
      } else {
        onError(result.message || 'Failed to record dispatch');
      }
    } catch {
      onError('Failed to record dispatch');
    } finally {
      setSaving(false);
    }
  };

  if (!cheque) return null;
  const chequeNumber = cheque.ChequeNumber || cheque.chequeNumber || '';
  const beneficiary = cheque.BeneficiaryName || cheque.beneficiaryName || '';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" TransitionComponent={DialogTransition}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
        <DispatchIcon sx={{ color: '#1D4ED8' }} />
        <Typography sx={{ fontWeight: 700, flex: 1 }}>Courier Dispatch</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cheque <strong>{chequeNumber}</strong> &mdash; {beneficiary}
        </Typography>
        <Stack spacing={2}>
          <TextField label="Courier Company" value={form.courierCompany} onChange={handleChange('courierCompany')} required fullWidth size="small" placeholder="e.g. Aramex, DHL" />
          <TextField label="Driver / Agent Name" value={form.driverName} onChange={handleChange('driverName')} required fullWidth size="small" placeholder="Name of courier person" />
          <TextField label="Tracking Reference" value={form.trackingReference} onChange={handleChange('trackingReference')} required fullWidth size="small" placeholder="Waybill or tracking number" />
          <TextField label="Remarks" value={form.remarks} onChange={handleChange('remarks')} fullWidth size="small" multiline rows={2} placeholder="Any notes (optional)" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none' }}>
          {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Dispatch'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Delivery Confirm Dialog ─────────────────────────────

const DeliveryDialog = ({ open, cheque, onClose, onSuccess, onError, service }) => {
  const [form, setForm] = useState({ receivedBy: '', signatureObtained: false, remarks: '' });
  const [saving, setSaving] = useState(false);

  const handleChange = (field) => (e) => {
    const value = e?.target?.type === 'checkbox' ? e.target.checked : (e?.target ? e.target.value : e);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.receivedBy.trim()) { onError('Received by name is required'); return; }
    if (!form.signatureObtained) { onError('Delivery signature confirmation is required'); return; }

    setSaving(true);
    try {
      const result = await service.confirmDelivery({
        collectionId: cheque.Id || cheque.id,
        deliveredDate: new Date().toISOString(),
        receivedBy: form.receivedBy.trim(),
        signatureObtained: form.signatureObtained,
        remarks: form.remarks.trim() || null,
      });
      if (result.success) {
        onSuccess(result.message || 'Delivery confirmed');
        setForm({ receivedBy: '', signatureObtained: false, remarks: '' });
      } else {
        onError(result.message || 'Failed to confirm delivery');
      }
    } catch {
      onError('Failed to confirm delivery');
    } finally {
      setSaving(false);
    }
  };

  if (!cheque) return null;
  const chequeNumber = cheque.ChequeNumber || cheque.chequeNumber || '';
  const beneficiary = cheque.BeneficiaryName || cheque.beneficiaryName || '';
  const courier = cheque.CourierCompany || cheque.courierCompany || '';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" TransitionComponent={DialogTransition}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
        <DeliverIcon sx={{ color: '#15803D' }} />
        <Typography sx={{ fontWeight: 700, flex: 1 }}>Confirm Delivery</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cheque <strong>{chequeNumber}</strong> &mdash; {beneficiary}
          {courier && <> (via {courier})</>}
        </Typography>
        <Stack spacing={2}>
          <TextField label="Received By" value={form.receivedBy} onChange={handleChange('receivedBy')} required fullWidth size="small" placeholder="Name of person who received" />
          <FormControlLabel control={<Checkbox checked={form.signatureObtained} onChange={handleChange('signatureObtained')} />} label="Delivery confirmed by recipient" />
          <TextField label="Remarks" value={form.remarks} onChange={handleChange('remarks')} fullWidth size="small" multiline rows={2} placeholder="Any notes (optional)" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', backgroundColor: '#15803D', '&:hover': { backgroundColor: '#166534' } }}>
          {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Confirm Delivered'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Return Dialog ───────────────────────────────────────

const MobileReturnDialog = ({ open, cheque, onClose, onSuccess, onError, service }) => {
  const [form, setForm] = useState({ returnReason: '', returnNotes: '' });
  const [saving, setSaving] = useState(false);

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.returnReason) { onError('Return reason is required'); return; }

    setSaving(true);
    try {
      const result = await service.markReturned({
        collectionId: cheque.Id || cheque.id || 0,
        chequeId: cheque.ChequeId || cheque.chequeId,
        returnDate: new Date().toISOString().split('T')[0],
        returnReason: form.returnReason,
        returnNotes: form.returnNotes.trim() || null,
      });
      if (result.success) {
        onSuccess(result.message || 'Cheque marked as returned');
        setForm({ returnReason: '', returnNotes: '' });
      } else {
        onError(result.message || 'Failed to mark as returned');
      }
    } catch {
      onError('Failed to mark as returned');
    } finally {
      setSaving(false);
    }
  };

  if (!cheque) return null;
  const chequeNumber = cheque.ChequeNumber || cheque.chequeNumber || '';
  const beneficiary = cheque.BeneficiaryName || cheque.beneficiaryName || '';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" TransitionComponent={DialogTransition}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
        <ReturnIcon sx={{ color: '#B91C1C' }} />
        <Typography sx={{ fontWeight: 700, flex: 1 }}>Mark as Returned</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Cheque <strong>{chequeNumber}</strong> &mdash; {beneficiary}
        </Typography>
        <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
          This cheque will revert to <strong>Printed</strong> status and can be re-dispatched.
        </Alert>
        <Stack spacing={2}>
          <TextField label="Return Reason" value={form.returnReason} onChange={handleChange('returnReason')} required fullWidth size="small" select>
            {RETURN_REASONS.map((reason) => (
              <MenuItem key={reason} value={reason}>{reason}</MenuItem>
            ))}
          </TextField>
          <TextField label="Additional Notes" value={form.returnNotes} onChange={handleChange('returnNotes')} fullWidth size="small" multiline rows={2} placeholder="Additional details (optional)" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" color="error" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none' }}>
          {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Mark Returned'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Detail Dialog ───────────────────────────────────────

const DetailDialog = ({ open, cheque, onClose, service }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const collectionId = cheque?.Id || cheque?.id || 0;

  useState(() => {
    if (open && collectionId) {
      setLoading(true);
      service.getCollectionDetail(collectionId).then((result) => {
        setDetail(result.success ? result.data : null);
        setLoading(false);
      }).catch(() => { setDetail(null); setLoading(false); });
    }
  }, [open, collectionId]);

  if (!cheque) return null;

  const chequeNumber = cheque.ChequeNumber || cheque.chequeNumber || '';
  const beneficiary = cheque.BeneficiaryName || cheque.beneficiaryName || '';
  const amount = cheque.ChqAmount ?? cheque.chqAmount ?? 0;
  const currency = cheque.Currency || cheque.currency || 'AED';
  const bankName = cheque.BankName || cheque.bankName || '';
  const chqDate = cheque.ChqDate || cheque.chqDate;
  const status = cheque.CollectionStatus || cheque.collectionStatus || 'Awaiting';
  const statusColor = STATUS_COLORS[status] || {};

  const formatCurrency = (amt) => `${currency} ${formatCurrencyAmount(amt, currency, { withSymbol: false })}`;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString() : '-';

  const InfoRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{value || '-'}</Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} fullScreen TransitionComponent={DialogTransition} PaperProps={{ sx: { backgroundColor: '#f8fafc' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5, px: 2, backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0' }}>
        <ViewIcon sx={{ color: '#64748b' }} />
        <Typography sx={{ fontWeight: 700, flex: 1 }}>Collection Details</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        {/* Cheque Info Card */}
        <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', mb: 1 }}>Cheque Information</Typography>
            <InfoRow label="Cheque No." value={chequeNumber} />
            <InfoRow label="Beneficiary" value={beneficiary} />
            <InfoRow label="Amount" value={formatCurrency(amount)} />
            <InfoRow label="Bank" value={bankName} />
            <InfoRow label="Cheque Date" value={formatDate(chqDate)} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>Status</Typography>
              <Chip label={status} size="small" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600, backgroundColor: statusColor.bg, color: statusColor.color }} />
            </Box>
          </CardContent>
        </Card>

        {/* Collection Details Card */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : detail ? (
          <>
            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', mb: 1 }}>Collection Details</Typography>
                {(detail.DeliveryMethod || detail.deliveryMethod) && <InfoRow label="Method" value={detail.DeliveryMethod || detail.deliveryMethod} />}
                {(detail.CollectorName || detail.collectorName) && <InfoRow label="Collector" value={detail.CollectorName || detail.collectorName} />}
                {(detail.CollectorId || detail.collectorId) && <InfoRow label="Collector ID" value={detail.CollectorId || detail.collectorId} />}
                {(detail.CourierCompany || detail.courierCompany) && <InfoRow label="Courier" value={detail.CourierCompany || detail.courierCompany} />}
                {(detail.DriverName || detail.driverName) && <InfoRow label="Driver" value={detail.DriverName || detail.driverName} />}
                {(detail.TrackingReference || detail.trackingReference) && <InfoRow label="Tracking Ref" value={detail.TrackingReference || detail.trackingReference} />}
                {(detail.ReceivedBy || detail.receivedBy) && <InfoRow label="Received By" value={detail.ReceivedBy || detail.receivedBy} />}
                {(detail.CollectionDate || detail.collectionDate) && <InfoRow label="Collection Date" value={formatDateTime(detail.CollectionDate || detail.collectionDate)} />}
                {(detail.DeliveredDate || detail.deliveredDate) && <InfoRow label="Delivered Date" value={formatDateTime(detail.DeliveredDate || detail.deliveredDate)} />}
                {(detail.ReturnReason || detail.returnReason) && <InfoRow label="Return Reason" value={detail.ReturnReason || detail.returnReason} />}
                {(detail.Remarks || detail.remarks) && <InfoRow label="Remarks" value={detail.Remarks || detail.remarks} />}
              </CardContent>
            </Card>

            {/* Timeline */}
            {(detail.Events || detail.events)?.length > 0 && (
              <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', mb: 1.5 }}>Timeline</Typography>
                  {(detail.Events || detail.events).map((event, idx) => {
                    const eventType = event.EventType || event.eventType;
                    const eventColor = EVENT_COLORS[eventType] || '#6B7280';
                    return (
                      <Box key={event.Id || event.id || idx} sx={{ display: 'flex', gap: 1.5, pb: 1.5, position: 'relative', '&:not(:last-child)::before': { content: '""', position: 'absolute', left: '5px', top: 18, bottom: 0, width: 2, backgroundColor: '#e2e8f0' } }}>
                        <DotIcon sx={{ fontSize: 12, mt: 0.5, color: eventColor, flexShrink: 0 }} />
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip label={eventType} size="small" sx={{ fontSize: '0.6rem', height: 20, backgroundColor: eventColor + '20', color: eventColor, fontWeight: 600 }} />
                            <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                              {formatDateTime(event.EventDate || event.eventDate)}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '0.72rem', color: '#64748b', mt: 0.25 }}>
                            by {event.PerformedBy || event.performedBy}
                          </Typography>
                          {(event.Notes || event.notes) && (
                            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.25 }}>{event.Notes || event.notes}</Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No collection record found. This cheque is awaiting collection.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MobileChequeCollections;
